import React from "react";
import PropTypes from "prop-types";

import { Checkbox, Icon, Label, Table } from "semantic-ui-react";

import useHub from "data/hub/useHub";

import { useAccess } from "components/Access";
import { FeatureFlag, useFeatures } from "components/FeatureFlag";
import Tooltip from "components/Tooltip";

import {
  useHubIntegrationSettings,
  useMeetingDatesSyncEligibility,
} from "./hooks";
import MeetingSyncEligibilityTooltip from "./components";

function HubIntegrationSettings({ userIntegrations }) {
  const hasAccess = useAccess();
  const hub = useHub();
  const { loading, toggle } = useHubIntegrationSettings();

  const syncMeetingsSelfConfigFF = useFeatures(
    "sync-meetings-self-service-enable-disable"
  );
  const {
    loadingEligibility,
    renderEligibilityTooltip,
    renderPendingChanges,
    eligibilityTooltipProps,
  } = useMeetingDatesSyncEligibility(
    hub.practiceId,
    Boolean(hub?.preferences?.sync?.shouldSyncMeetings)
  );

  return (
    <Table selectable={hasAccess(["Administrator"])} stackable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Functionality</Table.HeaderCell>
          <Table.HeaderCell width="4">Status</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <Table.Row>
          <Table.Cell>Save Completed Workflows to CRM Note</Table.Cell>
          <Table.Cell>
            <Checkbox
              toggle
              data-test="save-notes-toggle"
              checked={hub.logCompletedWorkflowsInCrm}
              onChange={toggle.notes}
              disabled={loading || !hasAccess(["Administrator"])}
            />
          </Table.Cell>
        </Table.Row>
        <FeatureFlag id="disable-crm-permission-toggle">
          <Table.Row>
            <Table.Cell>Disable CRM Permissions</Table.Cell>
            <Table.Cell>
              <Checkbox
                toggle
                data-test="crm-permissions-toggle"
                checked={!hub.useCrmPermissions}
                onChange={toggle.crmPermissions}
                disabled={loading || !hasAccess(["Administrator"])}
              />
            </Table.Cell>
          </Table.Row>
        </FeatureFlag>

        {syncMeetingsSelfConfigFF &&
          (userIntegrations.redtail || userIntegrations.wealthbox) && (
            <Table.Row>
              <Table.Cell>
                Meeting Dates Sync
                <Tooltip
                  hoverable
                  title={
                    <span>
                      If you disable this, you will no longer be able to sync
                      meetings dates from and to your CRM. Existing meetings
                      will not be affected.{" "}
                      <a
                        href={
                          eligibilityTooltipProps?.resourceLink ||
                          "https://help.myhubly.com"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read more.
                      </a>
                    </span>
                  }
                >
                  <Icon
                    data-test="disable-meeting-sync-tooltip"
                    style={{ fontSize: "1em", paddingLeft: "0.4rem" }}
                    name="info circle"
                    color="grey"
                    size="small"
                  />
                </Tooltip>
                {renderPendingChanges && (
                  <Tooltip
                    title={
                      "Please wait until your plan upgrade is completed. In the meantime, " +
                      "feel free to continue with your other tasks, and return to this page later."
                    }
                  >
                    <Label color="yellow" style={{ marginLeft: "0.4rem" }}>
                      Pending Changes
                    </Label>
                  </Tooltip>
                )}
              </Table.Cell>
              <Table.Cell>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    toggle
                    data-test="sync-meetings-toggle"
                    checked={Boolean(
                      hub?.preferences?.sync?.shouldSyncMeetings
                    )}
                    label={
                      renderEligibilityTooltip && (
                        <label style={{ fontSize: 0 }}>
                          Meeting Sync Toggle
                        </label>
                      )
                    }
                    onChange={toggle.meetings}
                    disabled={
                      loading ||
                      !hasAccess(["Administrator"]) ||
                      loadingEligibility ||
                      renderEligibilityTooltip ||
                      renderPendingChanges
                    }
                  />
                  {renderEligibilityTooltip && (
                    <MeetingSyncEligibilityTooltip
                      {...eligibilityTooltipProps}
                    />
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          )}
      </Table.Body>
    </Table>
  );
}

HubIntegrationSettings.propTypes = {
  userIntegrations: PropTypes.shape({
    redtail: PropTypes.bool,
    wealthbox: PropTypes.bool,
  }).isRequired,
};

HubIntegrationSettings.defaultProps = {};

export default HubIntegrationSettings;
